import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export interface WebsiteStudentConnectSubmission {
  id: string;
  name: string;
  phone: string;
  email: string;
  parent_name: string;
  parent_contact_info: string;
  school: string;
  grade: string;
  created_at?: string;
  is_read: boolean;
  read_at?: string | null;
}

export type WebsiteStudentConnectSubmissionUpdate = {
  name: string;
  phone: string;
  email: string;
  parent_name: string;
  parent_contact_info: string;
  school: string;
  grade: string;
};

export const useWebsiteStudentConnectSubmissions = (enabled = true) => {
  return useQuery<WebsiteStudentConnectSubmission[]>({
    queryKey: ['websiteStudentConnectSubmissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('website_student_connect_submissions')
        .select(
          'id, name, phone, email, parent_name, parent_contact_info, school, grade, created_at, is_read, read_at'
        )
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as WebsiteStudentConnectSubmission[]) ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
};

export const useUpdateWebsiteStudentConnectReadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isRead }: { id: string; isRead: boolean }) => {
      const updates = isRead
        ? { is_read: true, read_at: new Date().toISOString() }
        : { is_read: false, read_at: null };

      const { error } = await supabase
        .from('website_student_connect_submissions')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteStudentConnectSubmissions'] });
    },
  });
};

export const useUpdateWebsiteStudentConnectSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: WebsiteStudentConnectSubmissionUpdate;
    }) => {
      const { error } = await supabase
        .from('website_student_connect_submissions')
        .update({
          name: updates.name.trim(),
          phone: updates.phone.trim(),
          email: updates.email.trim(),
          parent_name: updates.parent_name.trim(),
          parent_contact_info: updates.parent_contact_info.trim(),
          school: updates.school.trim(),
          grade: updates.grade.trim(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websiteStudentConnectSubmissions'] });
    },
  });
};
